import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { api, apiError } from "@/lib/api";
import { useLang, lp, stripLang } from "@/lib/i18n";

const NAV = [
  { to: "/la-maison", fr: "La Maison", en: "The House" },
  { to: "/dames", fr: "Dames", en: "Dames" },
  { to: "/domino", fr: "Domino", en: "Domino" },
  { to: "/maroquinerie", fr: "Maroquinerie", en: "Leather Goods" },
  { to: "/contact", fr: "Contact", en: "Contact" },
];

const T = {
  fr: {
    banner: "Livraison offerte dès 150 € · Échantillons offerts · Séries limitées numérotées",
    menu: "Menu", close: "Fermer", openMenu: "Ouvrir le menu",
    tagline: "Une suite de collections, chacune régie par sa propre règle. La fournaise et la balance.",
    legal: "Mentions légales", cgv: "Conditions générales de vente",
    privacy: "Confidentialité", returns: "Politique de retour",
    newsletter: "Lettre de la maison", emailPlaceholder: "Ton adresse e-mail",
    subscribe: "S'inscrire", doubleOptIn: "Double opt-in. Un lien de confirmation te sera adressé.",
  },
  en: {
    banner: "Complimentary delivery from €150 · Samples with every order · Numbered limited series",
    menu: "Menu", close: "Close", openMenu: "Open menu",
    tagline: "A suite of collections, each governed by its own rule. The furnace and the balance.",
    legal: "Legal notice", cgv: "Terms of sale",
    privacy: "Privacy", returns: "Return policy",
    newsletter: "The House letter", emailPlaceholder: "Your e-mail address",
    subscribe: "Subscribe", doubleOptIn: "Double opt-in. A confirmation link will be sent to you.",
  },
};

const LangSwitch = ({ light }) => {
  const { pathname } = useLocation();
  const lang = useLang();
  const bare = stripLang(pathname);
  const cls = (active) =>
    `text-[12px] tracking-[0.2em] uppercase ${active ? (light ? "text-black" : "text-zinc-100") : (light ? "text-black/40" : "text-zinc-600")}`;
  return (
    <span className="flex items-center gap-2" data-testid="lang-switch">
      <Link to={bare} className={cls(lang === "fr")} aria-label="Version française">FR</Link>
      <span className={light ? "text-black/25" : "text-zinc-800"}>·</span>
      <Link to={lp("en", bare)} className={cls(lang === "en")} aria-label="English version">EN</Link>
    </span>
  );
};

const Header = ({ light }) => {
  const [open, setOpen] = useState(false);
  const lang = useLang();
  const t = T[lang];
  const wordmark = light ? "text-black" : "text-zinc-100";
  return (
    <header className={`sticky top-0 z-40 backdrop-blur-md border-b ${light ? "bg-[#f4f1ea]/85 border-black/10" : "bg-black/80 border-zinc-900"}`}>
      <div className={`w-full text-center text-[11px] sm:text-[12px] tracking-[0.28em] uppercase py-2.5 px-4 border-b ${light ? "text-black/45 border-black/10" : "text-zinc-500 border-zinc-900"}`} data-testid="top-banner">
        {t.banner}
      </div>
      <nav className="max-w-[1400px] mx-auto flex items-center justify-between px-5 sm:px-10 h-16">
        <button onClick={() => setOpen((v) => !v)} data-testid="menu-toggle"
          className={`md:hidden tracking-[0.2em] text-[13px] uppercase min-h-[44px] px-1 ${light ? "text-black" : "text-zinc-300"}`}
          aria-label={t.openMenu}>
          {open ? t.close : t.menu}
        </button>

        <div className="hidden md:flex gap-8 flex-1 items-center">
          {NAV.slice(0, 3).map((n) => (
            <NavLink key={n.to} to={lp(lang, n.to)} data-testid={`nav-${n.fr.toLowerCase().replace(/\s/g, "-")}`}
              className="val-link text-[13px] tracking-[0.24em] uppercase">{n[lang]}</NavLink>
          ))}
        </div>

        <Link to={lp(lang, "/")} data-testid="wordmark" className={`tracking-maison text-[15px] sm:text-[18px] uppercase whitespace-nowrap ${wordmark}`}>
          Maison&nbsp;Partita
        </Link>

        <div className="hidden md:flex gap-8 flex-1 justify-end items-center">
          {NAV.slice(3).map((n) => (
            <NavLink key={n.to} to={lp(lang, n.to)} data-testid={`nav-${n.fr.toLowerCase().replace(/\s/g, "-")}`}
              className="val-link text-[13px] tracking-[0.24em] uppercase">{n[lang]}</NavLink>
          ))}
          <LangSwitch light={light} />
        </div>
        <span className="md:hidden"><LangSwitch light={light} /></span>
      </nav>

      {open && (
        <div className={`md:hidden border-t px-5 py-6 flex flex-col gap-5 ${light ? "border-black/10" : "border-zinc-900"}`} data-testid="mobile-menu">
          {NAV.map((n) => (
            <NavLink key={n.to} to={lp(lang, n.to)} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.fr.toLowerCase().replace(/\s/g, "-")}`} className="val-link text-[15px] tracking-[0.24em] uppercase">{n[lang]}</NavLink>
          ))}
        </div>
      )}
    </header>
  );
};

const Footer = ({ light }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const lang = useLang();
  const t = T[lang];
  const border = light ? "border-black/10" : "border-zinc-900";
  const head = light ? "text-black" : "text-zinc-200";
  const muted = light ? "text-black/55" : "text-zinc-500";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message);
      setEmail("");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <footer className={`border-t ${border} mt-32`}>
      <div className="max-w-[1400px] mx-auto px-5 sm:px-10 py-20 grid gap-14 md:grid-cols-3">
        <div>
          <p className={`tracking-maison uppercase text-[15px] ${head}`}>Maison Partita</p>
          <p className={`mt-6 max-w-xs leading-relaxed ${muted}`}>{t.tagline}</p>
        </div>
        <div className="flex flex-col gap-3 text-[15px]">
          <Link to={lp(lang, "/mentions-legales")} className="val-link">{t.legal}</Link>
          <Link to={lp(lang, "/cgv")} className="val-link">{t.cgv}</Link>
          <Link to={lp(lang, "/confidentialite")} className="val-link">{t.privacy}</Link>
          <Link to={lp(lang, "/retours")} className="val-link">{t.returns}</Link>
        </div>
        <div>
          <p className={`text-[13px] tracking-[0.24em] uppercase ${light ? "text-black/60" : "text-zinc-400"}`}>{t.newsletter}</p>
          <form onSubmit={submit} className="mt-5" data-testid="newsletter-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder={t.emailPlaceholder} className="val-input" data-testid="newsletter-email" />
            <button type="submit" disabled={busy} className="val-btn mt-6 w-full sm:w-auto" data-testid="newsletter-submit">
              {busy ? "…" : t.subscribe}
            </button>
            <p className={`mt-4 text-[13px] leading-relaxed ${light ? "text-black/45" : "text-zinc-600"}`}>
              {t.doubleOptIn}
            </p>
          </form>
        </div>
      </div>
      <div className={`border-t ${border} py-6 text-center text-[12px] tracking-[0.2em] uppercase ${light ? "text-black/40" : "text-zinc-700"}`}>
        © MMXXVI — Maison Partita
      </div>
    </footer>
  );
};

export const Layout = () => {
  const { pathname } = useLocation();
  const bare = stripLang(pathname);
  const isDomino = bare.startsWith("/domino");
  const isHome = bare === "/";
  const light = isDomino || isHome;

  useEffect(() => {
    document.body.dataset.theme = isDomino ? "domino" : isHome ? "light" : "maison";
  }, [isDomino, isHome]);

  // hreflang : chaque page déclare son alternative FR/EN
  useEffect(() => {
    const alts = [
      { hreflang: "fr", href: "https://maison-partita.fr" + (bare === "/" ? "/" : bare) },
      { hreflang: "en", href: "https://maison-partita.fr" + lp("en", bare) },
      { hreflang: "x-default", href: "https://maison-partita.fr" + (bare === "/" ? "/" : bare) },
    ];
    const els = alts.map(({ hreflang, href }) => {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = hreflang;
      el.href = href;
      document.head.appendChild(el);
      return el;
    });
    return () => els.forEach((el) => el.remove());
  }, [bare]);

  return (
    <>
      <Header light={light} />
      <main className="min-h-screen">
        <Outlet />
      </main>
      <Footer light={light} />
      <Toaster theme={light ? "light" : "dark"} position="bottom-right" />
    </>
  );
};
